import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

/**
 * Axios instance for product category-related API calls
 * - Base URL `${API_BASE_URL}/api/v1`
 */
import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

/**
 * Axios instance for product category-related API calls
 * - Base URL `${API_BASE_URL}/api/v1`
 */
// Create axios instance for product category API calls
const productCategoryAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: warehouseParamsSerializer,
});

/**
 * Request interceptor
 * - Attaches stored auth token when present. Includes a short debug log
 *   which can help during development. Remove or gate this log in prod.
 */
// Add request interceptor to include auth token
productCategoryAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Debug log to verify token is being sent (remove in production)
      console.log('Sending Bearer token with productCategoryAPI request:', token.substring(0, 20) + '...');
    } else {
      console.warn('No auth token found in storage for productCategoryAPI request');
    }
    return applyWarehouseFilter(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * - Clears local/session storage and redirects to login on 401 responses
 */
// Add response interceptor to handle auth errors
productCategoryAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');

      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Normalize axios error objects into human-readable messages.
 * - Tries common server response shapes and falls back to generic text
 */
// Helper to extract backend error details from axios error

/**
 * Product Category Service Class
 * Handles all API operations related to product categories
 */
class ProductCategoryService {
  /**
   * Get all product categories with optional pagination and filtering
   * @param {Object} params - Query parameters (page, limit, search, sortBy, sortOrder)
   * @returns {Promise<Object>} Product categories list with pagination info
   */
  async getProductCategories(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Add pagination parameters - API expects 'skip' instead of 'page'
      if (params.page !== undefined) {
        const skip = (params.page - 1) * (params.limit || 20);
        queryParams.append('skip', skip);
      } else if (params.skip !== undefined) {
        queryParams.append('skip', params.skip);
      } else {
        queryParams.append('skip', 0); // Default skip value
      }

      if (params.limit) queryParams.append('limit', params.limit);
      else queryParams.append('limit', 20); // Default limit value

      if (params.search !== undefined) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await productCategoryAPI.get(`/product-categories?${queryParams.toString()}`);

      return {
        success: true,
        data: response.data.items || response.data.categories || response.data.data || response.data,
        pagination: response.data.pagination || {},
        total: response.data.total ?? response.data.count ?? response.data.length,
      };
    } catch (error) {
      console.error('Get product categories error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
        data: [],
      };
    }
  }

  /**
   * Get product category by ID
   * @param {number} id - Product category ID
   * @returns {Promise<Object>} Product category details
   */
  async getProductCategoryById(id) {
    try {
      const response = await productCategoryAPI.get(`/product-categories/${id}`);
      return {
        success: true,
        data: response.data.data || response.data,
      };
    } catch (error) {
      console.error(`Get product category ${id} error:`, error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
        data: null,
      };
    }
  }

  /**
   * Create a new product category
   * @param {Object} categoryData - Product category data {name, tax_id}
   * @returns {Promise<Object>} Created product category
   */
  async createProductCategory(categoryData) {
    try {
      const response = await productCategoryAPI.post('/product-categories', categoryData);
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message || undefined,
      };
    } catch (error) {
      console.error('Create product category error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Update an existing product category
   * @param {number} id - Product category ID
   * @param {Object} categoryData - Updated product category data
   * @returns {Promise<Object>} Updated product category
   */
  async updateProductCategory(id, categoryData) {
    try {
      const response = await productCategoryAPI.put(`/product-categories/${id}`, categoryData);
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message || undefined,
      };
    } catch (error) {
      console.error(`Update product category ${id} error:`, error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Delete a product category
   * @param {number} id - Product category ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteProductCategory(id) {
    try {
      const response = await productCategoryAPI.delete(`/product-categories/${id}`);
      return {
        success: true,
        data: response.data,
        message: response.data.message || undefined,
      };
    } catch (error) {
      console.error(`Delete product category ${id} error:`, error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Bulk delete multiple product categories
   * @param {Array<number>} ids - Array of product category IDs
   * @returns {Promise<Object>} Bulk deletion result
   */
  async bulkDeleteProductCategories(ids) {
    try {
      // API expects a top-level JSON array of ids in the request body, e.g. [2,3,4]
      const response = await productCategoryAPI.post('product-categories/bulk-delete', ids);
      return {
        success: true,
        data: response.data,
        message: response.data.message || `${ids.length} categories deleted successfully`,
      };
    } catch (error) {
      console.error('Bulk delete product categories error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Import product categories from Excel file
   * @param {File} file - Excel file containing product category data
   * @returns {Promise<Object>} Import result
   */
  async importProductCategories(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await productCategoryAPI.post('/product-categories/import/excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        success: true,
        data: response.data,
        message: response.data.message || undefined,
      };
    } catch (error) {
      console.error('Import product categories error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Export product categories to Excel
   * @param {Object} params - Export parameters (filters, etc.)
   * @returns {Promise<Blob>} Excel file blob
         message: response.data.message || undefined,
  async exportProductCategories(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.search !== undefined) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await productCategoryAPI.get(`/product-categories/export?${queryParams.toString()}`, {
        responseType: 'blob',
      });

      return {
        success: true,
        data: response.data,
        filename: response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'product_categories.xlsx',
      };
    } catch (error) {
      console.error('Export product categories error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Download sample Excel template for product category import
   * @returns {Promise<Blob>} Sample Excel file blob
   */
  async downloadSampleTemplate() {
    try {
      const response = await productCategoryAPI.get('/product-categories/sample-template', {
        responseType: 'blob',
      });

      return {
        success: true,
        data: response.data,
        filename: response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'product_categories_sample.xlsx',
      };
    } catch (error) {
      console.error('Download sample template error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }
}

// Export a singleton instance
const productCategoryService = new ProductCategoryService();
export default productCategoryService;
